import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { PatientsList, type PatientsQueryState } from '../../features/patients/patients-list';
import { useOperationsClient } from '../../lib/api/operations-client-context';
import { useSchedulingClient } from '../../lib/api/scheduling-client-context';

export const Route = createFileRoute('/patients/')({
  component: PatientsPage,
});

const CLINIC_ZONE = 'America/Los_Angeles';

/** `/patients` — the patient index (MASTER_PLAN M11.3, Docs/API.md §2.6). */
function PatientsPage() {
  const client = useOperationsClient();
  const scheduling = useSchedulingClient();

  const [query, setQuery] = useState<PatientsQueryState>({
    q: '',
    state: '',
    hasUpcoming: false,
    hasCard: false,
    sort: 'lastName',
  });
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const cursor = cursors[cursors.length - 1] ?? null;

  const settings = useQuery({
    queryKey: ['scheduling-settings'],
    queryFn: () => scheduling.getSettings(),
  });

  const patients = useQuery({
    queryKey: [
      'patients',
      query.q,
      query.state,
      query.hasUpcoming,
      query.hasCard,
      query.sort,
      cursor,
    ],
    queryFn: () =>
      client.listPatients({
        q: query.q,
        state: query.state === '' ? null : query.state,
        hasUpcoming: query.hasUpcoming,
        hasCard: query.hasCard,
        sort: query.sort,
        limit: 25,
        cursor,
      }),
  });

  return (
    <PatientsList
      rows={patients.data?.data ?? []}
      query={query}
      onQueryChange={(next) => {
        setQuery(next);
        setCursors([null]);
      }}
      clinicTimeZone={settings.data?.timeZone ?? CLINIC_ZONE}
      isLoading={patients.isPending}
      error={patients.isError ? (patients.error as Error).message : undefined}
      hasNext={patients.data?.nextCursor != null}
      hasPrevious={cursors.length > 1}
      onNext={() => {
        const next = patients.data?.nextCursor;
        if (next == null) return;
        setCursors((current) => [...current, next]);
      }}
      onPrevious={() => {
        setCursors((current) => (current.length > 1 ? current.slice(0, -1) : current));
      }}
      renderLink={(to, children, className): ReactNode => (
        <Link to={to} className={className}>
          {children}
        </Link>
      )}
    />
  );
}
